import {Component, h, EventEmitter, Event, Method, Prop, State, Watch, Host, Listen} from '@stencil/core';
import {RequestFieldGroup} from '../../classes/request_field_group';
import {DEFAULT_REQUEST_FIELDS} from '../../constants/request_fields';
import {CURRENT_STATE_T} from '../../types/current_state';
import {ITelephonyEventData, ITelephonyUpdate} from '../../interfaces/telephony';
import {SecureCallClientAPI} from '@securecall/client-api';
import {IConfigurationEvent, IConfigurationUpdate} from '../../interfaces/configuration';
import {ResponseFields} from '../../classes/response_fields';
import {DEFAULT_RESPONSE_FIELDS} from '../../constants/response_fields';
import {ITransactionData, ITransactionValues} from '../../interfaces/transaction';
import {PENDING_CALL_SECURE_TIMEOUT, AUTHENTICATION_RETRIES, PENDING_PAYMENT_TIMEOUT} from "../../constants/constants";
import {Logger} from "../../utils/logger";
import {mergeDeep} from "../../utils/utils";
import {CLIENT_COMP_VERSION} from "../../version";
import {IAuthenticationFailure} from "../../interfaces/authentication";


@Component({
  tag: 'securecall-client',
  styleUrl: 'securecall-client.css',
  shadow: true,
})
export class SecurecallClient {
  @Prop() apiLocation: string = window.origin;
  @Prop() awaitActiveCall: boolean = false;
  @Prop() theme: 'dark' | 'light' = 'light';
  @Prop() displayTransactionResult: boolean = true;
  @Prop() switchHint: string;
  @Prop() hideSecureButton: boolean = false;

  @State() currentState: CURRENT_STATE_T = CURRENT_STATE_T.NotAuthenticated;
  @State() errorMessage: string | undefined;
  @State() transactionToggle:boolean = false;

  @Event() authenticationSuccess: EventEmitter<string>;
  @Event() authenticationFailure: EventEmitter<IAuthenticationFailure>;
  @Event() configurationChanged: EventEmitter<IConfigurationUpdate>;
  @Event() callSecured: EventEmitter<boolean>;
  @Event() callEnded: EventEmitter<string>;
  @Event() transactionSubmitted: EventEmitter<ITransactionValues>;
  @Event() transactionSubmitting: EventEmitter<ITransactionData>;
  @Event() transactionSuccess: EventEmitter<ITransactionData>;
  @Event() transactionFailure: EventEmitter<ITransactionData>;
  @Event() loggedOut: EventEmitter<boolean>;
  // Telephony Events
  @Event() telephonyHold: EventEmitter<ITelephonyEventData>;
  @Event() telephonyRecover: EventEmitter<ITelephonyEventData>;
  @Event() telephonyLegA: EventEmitter<ITelephonyEventData>;
  @Event() telephonyLegB: EventEmitter<ITelephonyEventData>;
  @Event() telephonyValidate: EventEmitter<ITelephonyEventData>;

  private readonly _requestFields = new RequestFieldGroup(DEFAULT_REQUEST_FIELDS);
  private _responseFields = new ResponseFields(DEFAULT_RESPONSE_FIELDS);
  private client_api: SecureCallClientAPI
  private timeoutId: number | undefined;
  private readonly instanceId: number = Math.floor(Math.random() * 100000000)
  private authenticationCount: number = 0;
  private secureData: Record<string, unknown> | undefined;
  private currentSession: string | undefined;

  transactionElement: HTMLSecurecallTransactionElement | null = null;

  private readonly log = Logger('SecurecallClient', this.instanceId)

  private readonly boundProcessAuthenticationEvent = this.processAuthenticationEvent.bind(this);
  private readonly boundProcessStatusEvent         = this.processStatusEvent.bind(this);
  private readonly boundProcessConfigurationEvent  = this.processConfigurationEvent.bind(this);
  private readonly boundProcessTelephonyEvent      = this.processTelephonyEvent.bind(this);
  private readonly boundVisibilityEvent            = this.processVisibilityEvent.bind(this);

  connectedCallback() {
    this.log.debug(": connectedCallback");
    document.addEventListener('visibilitychange',       this.boundVisibilityEvent)

    this.client_api = new SecureCallClientAPI(this.apiLocation, `ClientComponent(${CLIENT_COMP_VERSION}/${this.instanceId})`)
    this.client_api.debug = true
    this.client_api.addEventListener('authentication',  this.boundProcessAuthenticationEvent)
    this.client_api.addEventListener('session',         this.boundProcessStatusEvent)
    this.client_api.addEventListener('configuration',   this.boundProcessConfigurationEvent)
    this.client_api.addEventListener('telephony',       this.boundProcessTelephonyEvent)

    // if (this.useCookie) {
    //   this.client_api.authenticate(this.username, this.password, this.useCookie);
    // } else {
    this.authenticationFailure.emit({count: this.authenticationCount, message: 'initialising'});
    // }
  }

  disconnectedCallback() {
    this.log.debug(": disconnectedCallback");
    document.removeEventListener('visibilitychange',       this.boundVisibilityEvent)

    this.client_api.removeEventListener('authentication', this.boundProcessAuthenticationEvent)
    this.client_api.removeEventListener('session',        this.boundProcessStatusEvent)
    this.client_api.removeEventListener('configuration',  this.boundProcessConfigurationEvent)
    this.client_api.removeEventListener('telephony',      this.boundProcessTelephonyEvent)
    this.client_api.disconnect();
  }

  componentWillLoad() {
    this.log.debug(": componentWillLoad");
  }

  processVisibilityEvent() {
    this.log.debug(": processVisibilityEvent");
    this.transactionToggle = !this.transactionToggle
  }

  @Listen('secureFieldResetEvent')
  handleSecureFieldResetEvent(evt: CustomEvent<string>) {
    this.log.debug('secureFieldResetEvent: resetting ', evt.detail);
    this.client_api.reset([evt.detail])
      .then(() => {
        this.log.debug('secureFieldResetEvent: reset ');
      })
      .catch(e => {
        this.log.debug('secureFieldResetEvent: FAILED: ', e);
      });
  }

  @Listen('submitTransactionEvent')
  handleSubmitTransactionEvent(evt: CustomEvent<ITransactionValues>) {
    this.log.debug('submitTransactionEvent: ', evt.detail);
    this.submitTransaction(evt.detail);
  }

  @Listen('responseButtonEvent')
  handleResponseButtonEvent(evt: CustomEvent<string>) {
    this.log.debug('responseButtonEvent: ', evt.detail);
    switch (evt.detail) {
      case 'retryClearCVV':
        this.triggerAnotherTransaction(false, ['cvv']);
        break;
      case 'retryClearSecure':
        this.triggerAnotherTransaction(false, ['all']);
        break;
      case 'retryClearAll':
        this.triggerAnotherTransaction(true, ['all']);
        break;
      default:
        this.log.debug('responseButtonEvent: unknown action: ', evt.detail);
    }
  }

  @Listen('message')
  messageHandler(event: MessageEvent) {
    this.log.debug("messageHandler: popup return= ",event);
    if (event.origin !== window.origin) return;
    this.log.debug("messageHandler: origin matches");
  }

  @Watch('currentState')
  currentStateWatcher(newValue: CURRENT_STATE_T, oldValue: CURRENT_STATE_T) {
    this.log.debug('currentStateWatcher', oldValue, ' => ', newValue);
    if (newValue !== oldValue) {
      this.cancelTimeout();
      this.errorMessage = undefined;
    }
  }

  @Watch('awaitActiveCall')
  watchAwaitActiveCall(newValue: boolean, oldValue: boolean) {
    this.log.debug('awaitActiveCall: ', oldValue, ' => ', newValue);
  }


  @Method()
  async triggerAnotherTransaction(resetTransactionDetails = true, resetCardFields = ['cvv']): Promise<void> {
    this.log.debug('Method: triggerAnotherTransaction resetTransactionDetails=' + resetTransactionDetails + ' resetCardFields=' + JSON.stringify(resetCardFields));
    if (this.currentState === CURRENT_STATE_T.Success || this.currentState === CURRENT_STATE_T.Failure) {
      if (resetTransactionDetails) {
        this._requestFields.clearNonSecureFields();
      }
      if (resetCardFields && resetCardFields.length > 0) {
        await this.client_api.reset(resetCardFields).catch(() => {});
      }
      this.currentState = CURRENT_STATE_T.Secured;
    } else {
      this.log.debug('Method: triggerAnotherTransaction Incompatible state: ', this.currentState);
      throw new Error('Incompatible state: ' + this.currentState);
    }
  }

  @Method()
  async submitAnotherTransaction(data: ITransactionValues): Promise<boolean> {
    this.log.debug('submitAnotherTransaction: ');
    if (this.currentState === CURRENT_STATE_T.Success || this.currentState === CURRENT_STATE_T.Failure) {
      this.submitTransaction(data);
      return true;
    } else {
      this.log.debug('Method: submitAnotherTransaction Wrong state: ', this.currentState);
      return false;
    }
  }

  @Method()
  async authenticate(username?: string, password?: string, useCookie?: boolean): Promise<void> {
    this.log.debug('Method: authenticate');
    try {
      await this.client_api.authenticate(username, password, useCookie);
    } catch (e) {
      this.log.debug('Method: authenticate failed with error: ', e);
      throw e;
    }
  }

  @Method()
  async logout(): Promise<void> {
    this.log.debug('Method: logout');

    this.client_api.logout()
      .then(() => {
        this.log.debug('Method: logout success');
        this.currentState = CURRENT_STATE_T.NotAuthenticated;
        this.loggedOut.emit(true);
      })
      .catch((e) => {
        this.log.debug('Method: logout failed with error: ' + e);
      })
  }

  @Method()
  async secure(): Promise<void> {
    this.log.debug('Method: secure');
    const body = (this.switchHint) ? {switch_hint: this.switchHint} : {}
    await this.client_api.secure(body);
  }

  @Method()
  async release(): Promise<void> {
    this.log.debug('Method: release');
    await this.client_api.release();
  }

  @Method()
  async updateTelephony(data: ITelephonyUpdate): Promise<void> {
    this.log.debug(': updateTelephony: ', data);
    return this.client_api.telephony(data);
  }

  @Method()
  async updateRequestFields(updates: (arg0: any) => void) {
    updates(this._requestFields);
    this.generateConfigurationChangedEvent();
    this.transactionToggle = !this.transactionToggle
  }

  @Method()
  async updateSessionRequestFields(updates: (arg0: any) => void) {
    updates(this._requestFields.session);
    this.generateConfigurationChangedEvent();
    this.transactionToggle = !this.transactionToggle
  }

  @Method()
  async updateTransactionRequestFields(updates: (arg0: any) => void) {
    updates(this._requestFields.transaction);
    this.generateConfigurationChangedEvent();
    this.transactionToggle = !this.transactionToggle
  }

  @Method()
  async updateResponseFields(updates: (arg0: any) => void) {
    updates(this._responseFields.defaults);
    this.generateConfigurationChangedEvent();
    this.transactionToggle = !this.transactionToggle
  }

  processAuthenticationEvent(evt: any) {
    if (evt.success) {
      this.log.debug('Authentication Success message: ', evt)
      this.currentState = CURRENT_STATE_T.Idle;
      this.authenticationSuccess.emit();
    } else {
      this.log.debug('Authentication Failure message: ', JSON.stringify(evt))
      this.handleAuthenticationFailure(evt.reason, evt.code)
    }
  }

  processConfigurationEvent(evt: CustomEvent<IConfigurationEvent>) {
    this.log.debug(": processConfigurationEvent: ", evt);
    const settings = evt.detail.settings;
    if(typeof settings?.show_gateways !== 'undefined') {
      this._requestFields.instance.gatewayName.hidden = !(settings.show_gateways === true);
    }
    if(evt.detail.gateways) {
      this._requestFields.instance.gatewayName.possibleValues = { ...evt.detail.gateways };
    }
    if(settings?.request_fields) {
      const { _transactionUpdate, _sessionUpdate, ...requestFieldsUpdate } = settings.request_fields;
      Object.entries(requestFieldsUpdate).forEach(([key, value]) => {
        if(!this._requestFields.fieldNames.includes(key)) {
          // Add the field
          this._requestFields.fieldNames.push(key);
        }
        let section = this._requestFields.instance;
        if(_sessionUpdate) {
          section = this._requestFields.session;
        } else if(_transactionUpdate) {
          section = this._requestFields.transaction;
        }
        let f = section[key];
        if(f) {
          Object.entries(value).forEach(([k2, v2]) => {
            f[k2] = v2
          })
        }
      })
    }
    if(settings?.response_fields) {
      this._responseFields = new ResponseFields(mergeDeep(DEFAULT_RESPONSE_FIELDS, settings.response_fields))
    }
    this.generateConfigurationChangedEvent();
    this.transactionToggle = !this.transactionToggle;
  }

  generateConfigurationChangedEvent() {
    this.configurationChanged.emit({
      requestFields: this._requestFields.calculatedFields(),
      responseFields: this._responseFields.calculatedFields()
    });
  }

  processStatusEvent(j: any) {
    this.log.debug(": processStatusEvent: ", j);
    switch (j.state) {
      case 'securing':
        if (this.currentState === CURRENT_STATE_T.Idle) {
          this.currentState = CURRENT_STATE_T.Securing;
          this.startTimeout(PENDING_CALL_SECURE_TIMEOUT, "Timeout: Securing Call");
        } else if (this.currentState === CURRENT_STATE_T.Securing) {
          this.log.debug('>>>> getting duplicate state: ', this.currentState);
        } else {
          this.log.error('>>>> ERROR: attempting to secure while in state: ', this.currentState);
        }
        break;
      case 'collection':
        if (j.call_data.state === 'secured') {
          if (this.currentState === CURRENT_STATE_T.Securing || this.currentState === CURRENT_STATE_T.Idle) {
            this.currentState = CURRENT_STATE_T.Secured;
            this.callSecured.emit(true);
          }
        }
        this.secureData = j.secure_data;
        this.currentSession = j.session;
        this._requestFields.processSecureData(j.secure_data);
        this.transactionToggle = !this.transactionToggle;
        break;
      case 'transaction':
        if (this.currentState !== CURRENT_STATE_T.Submitting && this.currentState !== CURRENT_STATE_T.Success && this.currentState !== CURRENT_STATE_T.Failure) {
          this.currentState = CURRENT_STATE_T.Secured;
          this.callSecured.emit(true);
        }
        switch (j.transaction_data.state) {
          case 'submitting':
            this.transactionSubmitting.emit({...j.transaction_data, secureData: j.secure_data, session: j.session});
            this.currentState = CURRENT_STATE_T.Submitting;
            break;
          case 'success':
            this.cancelTimeout();
            this._responseFields.processResponse(j.transaction_data);
            this.transactionSuccess.emit({...j.transaction_data, secureData: j.secure_data, session: j.session});
            this.currentState = CURRENT_STATE_T.Success;
            break;
          case 'failure':
            this.cancelTimeout();
            this._responseFields.processResponse(j.transaction_data);
            this.transactionFailure.emit({...j.transaction_data, secureData: j.secure_data, session: j.session});
            this.currentState = CURRENT_STATE_T.Failure;
            break;
          default:
            this.log.debug('>>>> processStatusEvent: unknown transaction state:', j.transaction_data.state);
            break;
        }
        break;
      case 'ended':
        this.currentState = CURRENT_STATE_T.Idle;
        this._requestFields.resetSession();
        this._requestFields.resetTransaction();
        this.currentSession = undefined;
        this.secureData = undefined;
        this.callSecured.emit(false);
        if (j.call_data?.error) {
          this.errorMessage = j.call_data.error;
          this.callEnded.emit(j.call_data.error);
        } else {
          this.callEnded.emit("call ended successfully");
        }
        break;
      default:
        this.log.debug('unknown state:' + j.state);
    }
  }

  processTelephonyEvent(j: any) {
    this.cancelTimeout();
    this.log.debug(": processTelephonyEvent: ", j);
    switch (j.command) {
      case 'hold':
        this.telephonyHold.emit({session: j.session})
        break;
      case 'recover':
        this.telephonyRecover.emit({session: j.session})
        break;
      case 'leg_a':
        this.telephonyLegA.emit({session: j.session, dnis: j.dnis})
        break;
      case 'leg_b':
        this.telephonyLegB.emit({session: j.session, dnis: j.dnis})
        break;
      case 'validate':
        this.telephonyValidate.emit({session: j.session})
        break;
      default:
        this.log.debug(": processTelephonyEvent: unknown telephony command: ", j.command);
    }
  }

  handleAuthenticationFailure(status: string, code: number) {
    this.log.debug(': intAuthenticationFailureHandler: Received the custom intAuthenticationFailure event: ', code, ':', status);
    // Reset state
    this.currentState = CURRENT_STATE_T.NotAuthenticated;

    switch (code) {
      case 0: // initial event, shouldn't show a message
        break;
      case 401:
        this.errorMessage = "Unauthorised. Please attempt to log in again. Ensure that your browser is not in incognito mode";
        break;
      case 403:
        this.errorMessage = "Configuration error. Please contact your administrator";
        break;
      default:
        this.errorMessage = (code) ? code + ':' + status : status;
    }

    this.authenticationCount++;
    if (this.authenticationCount > AUTHENTICATION_RETRIES) {
      this.errorMessage = "Unable to connect to the server. Please contact your administrator";
    } else {
      this.authenticationFailure.emit({count: this.authenticationCount, message: this.errorMessage});
    }
    this.log.debug(': intAuthenticationFailureHandler: ', this.errorMessage);
  }

  submitTransaction(eventData: ITransactionValues) {
    // reflect the values back into paymentDetails
    this.log.debug("submitTransaction: eventData: " + JSON.stringify(eventData));

    this.startTimeout(PENDING_PAYMENT_TIMEOUT, "Timeout: Processing Transaction");

    this.currentState = CURRENT_STATE_T.Submitting;

    this.client_api.submit(eventData)
      .then(() => {
        this.transactionSubmitted.emit(eventData);
      })
      .catch((error: any) => {
        this.log.debug("submitTransaction: error: " + error.message + '(' + error.code + ')');
        const data: ITransactionData = {
          state: 'failure',
          request: eventData,
          response: {error_message: error.message, error_code: error.code},
          secureData: this.secureData,
          session: this.currentSession
        };
        this.currentState = CURRENT_STATE_T.Failure;
        this.transactionFailure.emit(data);
        this.errorMessage = 'Submit Response: ' + error.message + ' (' + error.code + ')';
      })
  }

  ////////////////////////////
  // Timeout Methods
  ////////////////////////////

  startTimeout(milliseconds: number, message: string) {
    this.timeoutId = window.setTimeout(() => {
      this.log.debug('>>>> Firing timeout : currentState:', this.currentState, ' destinationState:');
      this.errorMessage = message;
      this.client_api.connect()
    }, milliseconds);
    this.log.debug('>>>> Starting timeout id:', this.timeoutId, ' currentState:', this.currentState);
  }

  cancelTimeout() {
    if (this.timeoutId) {
      this.log.debug('>>>> Canceling timeout id:', this.timeoutId, ' currentState:', this.currentState);
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  ////////////////////////////
  // Render Methods
  ////////////////////////////

  private renderResponseFields(success: boolean) {
    const fields = this._responseFields.asComponentChildren(success);
    if (!this.displayTransactionResult) return null;
    return (
      <form class="response-form">
        <div class="response-result-container">
          <p>{success ? 'Transaction Accepted' : 'Transaction Declined'}</p>
        </div>
        <div class="response-field-container">
          {fields.map((field) => {
            const Tag = 'securecall-response-' + field.component;
            return <Tag id={field.name} field-name={field.name} label={field.label} value={field.value}></Tag>;
          })}
        </div>
      </form>
    )
  }


  currentStateRender() {
    switch (this.currentState) {
      case CURRENT_STATE_T.NotAuthenticated:
        return null;
      case CURRENT_STATE_T.Idle:
        if(this.hideSecureButton)
          return null
        else
          return (
            <div class="secure-form">
              <p class="component-text">&nbsp;</p>
              {this.awaitActiveCall ? (
                <button type="button" class="component-button" disabled>Awaiting Active Call</button>
              ) : (
                <button
                  type="button" class="component-button"
                  onClick={() => this.secure()}>
                  Press to Secure
                </button>
              )}
            </div>
          )
      case CURRENT_STATE_T.Securing:
        return (
          <form>
            <div class="component-field-container">
              <div class="loading-spinner">
                <div class="spinner-circle"></div>
              </div>
            </div>
          </form>
        )
      case CURRENT_STATE_T.Secured:
        return (
          <securecall-transaction id="transaction"
                                  transactionToggle={this.transactionToggle}
                                  fieldData={this._requestFields}
                                  ref={(el) => this.transactionElement = el}></securecall-transaction>
        )
      case CURRENT_STATE_T.Submitting:
        return (
          <div class="component-field-container">
            <div class="loading-spinner">
              <div class="spinner-circle"></div>
            </div>
          </div>
        )
      case CURRENT_STATE_T.Failure:
        return this.renderResponseFields(false)
      case CURRENT_STATE_T.Success:
        return this.renderResponseFields(true)
      default:
        return (
          <form>
            <div class="component-field-container">
              <p>&nbsp;{this.currentState}</p>
            </div>
          </form>
        )
    }
  };

  renderErrorMessage() {
    if (this.errorMessage) {
      const message = this.errorMessage;
      return (
        <div class="component-field-container">
          <p>{message}</p>
        </div>
      );
    }
    return null;
  }

  render() {
    return this.currentState !== CURRENT_STATE_T.NotAuthenticated ? (
      <Host>
        <div class={`theme-${this.theme}`}>
          {this.currentStateRender()}
          {this.renderErrorMessage()}
        </div>
      </Host>
    ) : null;
  }
}
